<template>
  <div
    class="relative"
   
  >
    <button
      type="button"
      class="absolute -top-1 -left-1.5 flex items-center justify-center p-1.5 text-gray-400 cursor-pointer hover:text-gray-500"
      :disabled="!canMoveToPreviousMonth"
     
      @click="handlePreviousMonth"
    >
      <TIcon :icon="['fal', 'chevron-left']" />
    </button>

    <button
      type="button"
      class="absolute -top-1 -right-1.5 flex items-center justify-center p-1.5 text-gray-400 cursor-pointer hover:text-gray-500"
      :disabled="!canMoveToNextMonth"
     
      @click="handleNextMonth"
    >
      <TIcon :icon="['fal', 'chevron-right']" />
    </button>

    <div
      :class="[
        'mx-auto grid grid-cols-1 gap-x-6 gap-y-12',
        computedGridClasses,
      ]"
    >
      <DatePickerMonth
        v-for="month in months"
        :key="month.toISOString()"
        :month="month.month()"
        :year="month.year()"
        :selected-dates="selectedDates"
        :timezone="timezone"
        :month-name-format="monthNameFormat"
        :min-date="minDate"
        :max-date="maxDate"
        :unavailable-days="unavailableDays"
        :disabled-dates="disabledDates"
        class="w-full min-w-[max-content]"
        @date-click="emit('date-click', $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Dayjs } from 'dayjs'
import type { BlockedRange, DaysOfWeek } from '~/types'

const props = withDefaults(defineProps<{
  modelValue?: Dayjs | Dayjs[]
  oneColumn?: boolean
  timezone?: string
  minDate?: Dayjs
  maxDate?: Dayjs
  monthNameFormat?: string
  ignoreLeapYear?: boolean
  disabledDates?: BlockedRange[]
  unavailableDays?: DaysOfWeek[]
}>(), {
  maxDate: () => datetime().add(2, 'year').endOf('month'),
})

const emit = defineEmits<{
  'date-click': [date: Dayjs]
}>()

const selectedDates = computed(() => {
  if (!props.modelValue) {
    return []
  }

  if (Array.isArray(props.modelValue)) {
    return props.modelValue
  }

  return [props.modelValue]
})

const computedGridClasses = computed(() => {
  return {
    'sm:grid-cols-[repeat(2,auto)]': !props.oneColumn,
  }
})

const calendarMonth = ref((selectedDates.value?.[0] ?? datetime()).startOf('month'))

const canMoveToPreviousMonth = computed(() => {
  return calendarMonth.value.isAfter(props.minDate)
})

const canMoveToNextMonth = computed(() => {
  return calendarMonth.value.endOf('month').isBefore(props.maxDate)
})

function handlePreviousMonth() {
  if (!canMoveToPreviousMonth.value) {
    return
  }

  calendarMonth.value = calendarMonth.value.subtract(1, 'month')
}

function handleNextMonth() {
  if (!canMoveToNextMonth.value) {
    return
  }

  calendarMonth.value = calendarMonth.value.add(1, 'month')
}

const months = computed(() => {
  const length = props.oneColumn ? 1 : 2
  return Array.from({ length }, (_, index) => calendarMonth.value.add(index, 'month'))
})
</script>
